/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetRemoteMcpServerRequest,
  GetRemoteMcpServerSecurity,
} from "../models/operations/getremotemcpserver.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetRemoteMcpServerQuery,
  GetRemoteMcpServerQueryData,
  prefetchGetRemoteMcpServer,
  queryKeyGetRemoteMcpServer,
} from "./getRemoteMcpServer.core.js";
export {
  buildGetRemoteMcpServerQuery,
  type GetRemoteMcpServerQueryData,
  prefetchGetRemoteMcpServer,
  queryKeyGetRemoteMcpServer,
};

export type GetRemoteMcpServerQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getServer remoteMcp
 *
 * @remarks
 * Get a remote MCP server by ID or slug. Exactly one of id or slug must be provided.
 */
export function useGetRemoteMcpServer(
  request?: GetRemoteMcpServerRequest | undefined,
  security?: GetRemoteMcpServerSecurity | undefined,
  options?: QueryHookOptions<
    GetRemoteMcpServerQueryData,
    GetRemoteMcpServerQueryError
  >,
): UseQueryResult<GetRemoteMcpServerQueryData, GetRemoteMcpServerQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGetRemoteMcpServerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getServer remoteMcp
 *
 * @remarks
 * Get a remote MCP server by ID or slug. Exactly one of id or slug must be provided.
 */
export function useGetRemoteMcpServerSuspense(
  request?: GetRemoteMcpServerRequest | undefined,
  security?: GetRemoteMcpServerSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetRemoteMcpServerQueryData,
    GetRemoteMcpServerQueryError
  >,
): UseSuspenseQueryResult<
  GetRemoteMcpServerQueryData,
  GetRemoteMcpServerQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetRemoteMcpServerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetRemoteMcpServerData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id?: string | undefined;
      slug?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: GetRemoteMcpServerQueryData,
): GetRemoteMcpServerQueryData | undefined {
  const key = queryKeyGetRemoteMcpServer(...queryKeyBase);

  return client.setQueryData<GetRemoteMcpServerQueryData>(key, data);
}

export function invalidateGetRemoteMcpServer(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id?: string | undefined;
      slug?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteMcp", "getServer", ...queryKeyBase],
  });
}

export function invalidateAllGetRemoteMcpServer(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteMcp", "getServer"],
  });
}
